import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";

interface PriceData {
  name: string;
  platform: string;
  imageUrl: string;
  url: string;
  prices: {
    loose: string | null;
    complete: string | null;
    new: string | null;
    graded: string | null;
    boxOnly: string | null;
    manualOnly: string | null;
  };
}

export interface CheckedGrade {
  productName: string;
  platform: string;
  imageUrl: string;
  photoUrl: string | null;
  priceType: string;
  originalPrice: string;
  grade: string;
  gradePrice: string;
}

interface PriceDisplayProps {
  data: PriceData;
  photoUrl?: string | null;
  onCheckedChange?: (checked: CheckedGrade[]) => void;
}

const priceCards = [
  { key: "loose" as const, label: "LOOSE", icon: "📦" },
  { key: "complete" as const, label: "COMPLETE", icon: "✅" },
  { key: "new" as const, label: "NEW", icon: "🆕" },
];

const grades = [
  { label: "GRADE A", pct: 0.55 },
  { label: "GRADE B", pct: 0.35 },
  { label: "GRADE C", pct: 0.20 },
];

const PriceDisplay = ({ data, photoUrl, onCheckedChange }: PriceDisplayProps) => {
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  const calcPct = (price: string | null, pct: number) => {
    if (!price) return null;
    const num = parseFloat(price.replace(/,/g, ""));
    if (isNaN(num)) return null;
    return (num * pct).toFixed(2);
  };

  const toggleCheck = (key: string, priceType: string, originalPrice: string, grade: string, gradePrice: string) => {
    const newChecked = { ...checked, [key]: !checked[key] };
    setChecked(newChecked);

    if (onCheckedChange) {
      const selected: CheckedGrade[] = [];
      // Rebuild selected from all price cards & grades
      for (const card of priceCards) {
        const price = data.prices[card.key];
        if (!price) continue;
        for (const g of grades) {
          const k = `${card.key}-${g.label}`;
          const val = calcPct(price, g.pct);
          if (newChecked[k] && val) {
            selected.push({
              productName: data.name,
              platform: data.platform,
              imageUrl: data.imageUrl,
              photoUrl: photoUrl || null,
              priceType: card.label,
              originalPrice: price,
              grade: g.label,
              gradePrice: val,
            });
          }
        }
      }
      onCheckedChange(selected);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Product Header */}
      <div className="flex items-start gap-4 p-4 bg-card rounded-lg border border-border glow-green">
        {(photoUrl || data.imageUrl) && (
          <img
            src={photoUrl || data.imageUrl}
            alt={data.name}
            className="w-20 h-20 object-contain rounded border border-border bg-muted"
          />
        )}
        <div className="flex-1 min-w-0">
          <h2 className="font-bold text-foreground text-lg leading-tight truncate">
            {data.name}
          </h2>
          {data.platform && (
            <span className="inline-block mt-1 px-2 py-0.5 bg-primary/20 text-primary text-xs font-mono rounded">
              {data.platform}
            </span>
          )}
        </div>
      </div>

      {/* Price Grid */}
      <div className="grid grid-cols-2 gap-3">
        {priceCards.map(({ key, label, icon }) => {
          const price = data.prices[key];
          const hasPrice = price !== null;
          return (
            <div
              key={key}
              className="relative p-3 bg-card rounded-lg border border-border hover:border-primary/50 transition-all group"
            >
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-sm">{icon}</span>
                <span className="text-[10px] font-mono text-muted-foreground tracking-widest uppercase">
                  {label}
                </span>
              </div>
              <p className="text-xl font-bold font-mono text-foreground group-hover:text-accent transition-colors">
                {price ? `$${price}` : "N/A"}
              </p>
              {hasPrice && (
                <div className="mt-2 px-2 py-2 bg-primary/10 border border-primary/30 rounded space-y-2">
                  {grades.map(({ label: gLabel, pct }) => {
                    const val = calcPct(price, pct);
                    const checkKey = `${key}-${gLabel}`;
                    return (
                      <div key={gLabel} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={!!checked[checkKey]}
                            onCheckedChange={() =>
                              toggleCheck(checkKey, label, price!, gLabel, val || "0")
                            }
                            className="border-primary data-[state=checked]:bg-primary"
                          />
                          <span className="text-sm font-mono font-semibold text-muted-foreground">{gLabel}</span>
                        </div>
                        <span className="text-base font-bold font-mono text-primary">${val}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PriceDisplay;
